import { Minus, Pencil, Plus } from "lucide-react";

import { type DiffEntry, diffJson, formatPath, formatValue } from "@/lib/diff";

interface Props {
  before: unknown;
  after: unknown;
  /** "side" hiển thị 2 cột; "inline" hiển thị 1 cột compact. */
  layout?: "side" | "inline";
}

const KIND_META: Record<DiffEntry["kind"], { color: string; label: string; icon: JSX.Element }> = {
  added: {
    color: "bg-emerald-50 text-emerald-900 border-emerald-300",
    label: "added",
    icon: <Plus className="size-3" />,
  },
  removed: {
    color: "bg-rose-50 text-rose-900 border-rose-300",
    label: "removed",
    icon: <Minus className="size-3" />,
  },
  changed: {
    color: "bg-amber-50 text-amber-900 border-amber-300",
    label: "changed",
    icon: <Pencil className="size-3" />,
  },
};

export function AuditDiffViewer({ before, after, layout = "side" }: Props): JSX.Element {
  const diffs = diffJson(before, after);

  if (layout === "inline") {
    if (diffs.length === 0) {
      return (
        <p className="text-xs text-muted-foreground italic">
          Không có thay đổi.
        </p>
      );
    }
    return (
      <div className="space-y-1">
        {diffs.map((d, i) => {
          const meta = KIND_META[d.kind];
          return (
            <div
              key={i}
              className={`rounded border px-2 py-1 text-xs flex items-start gap-2 ${meta.color}`}
            >
              {meta.icon}
              <code className="font-mono text-[10px]">{formatPath(d.path)}</code>
              <span className="text-[10px] uppercase font-semibold">{meta.label}</span>
              <div className="flex-1 min-w-0">
                {d.kind === "changed" ? (
                  <div className="space-y-0.5 font-mono text-[11px]">
                    <div className="text-rose-700 truncate">- {formatValue(d.before)}</div>
                    <div className="text-emerald-700 truncate">+ {formatValue(d.after)}</div>
                  </div>
                ) : d.kind === "added" ? (
                  <div className="text-emerald-700 font-mono text-[11px] truncate">
                    {formatValue(d.after)}
                  </div>
                ) : (
                  <div className="text-rose-700 font-mono text-[11px] truncate">
                    {formatValue(d.before)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Side-by-side full JSON
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className="text-[10px] uppercase font-semibold text-muted-foreground mb-1">
          Before
        </div>
        <pre className="rounded border bg-rose-50 text-rose-900 p-2 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap break-words max-h-96">
          {before ? JSON.stringify(before, null, 2) : "(none)"}
        </pre>
      </div>
      <div>
        <div className="text-[10px] uppercase font-semibold text-muted-foreground mb-1">
          After
        </div>
        <pre className="rounded border bg-emerald-50 text-emerald-900 p-2 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap break-words max-h-96">
          {after ? JSON.stringify(after, null, 2) : "(none)"}
        </pre>
      </div>

      <div className="col-span-2 mt-2">
        <div className="text-[10px] uppercase font-semibold text-muted-foreground mb-1">
          Changes ({diffs.length})
        </div>
        {diffs.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">Không có thay đổi.</p>
        ) : (
          <div className="space-y-1">
            {diffs.map((d, i) => {
              const meta = KIND_META[d.kind];
              return (
                <div
                  key={i}
                  className={`rounded border px-2 py-1 text-xs flex items-start gap-2 ${meta.color}`}
                >
                  {meta.icon}
                  <code className="font-mono text-[10px]">{formatPath(d.path)}</code>
                  <span className="text-[10px] uppercase font-semibold">{meta.label}</span>
                  {d.kind === "changed" ? (
                    <div className="flex-1 min-w-0 space-y-0.5 font-mono text-[11px]">
                      <div className="text-rose-700 truncate">- {formatValue(d.before)}</div>
                      <div className="text-emerald-700 truncate">+ {formatValue(d.after)}</div>
                    </div>
                  ) : d.kind === "added" ? (
                    <div className="flex-1 min-w-0 text-emerald-700 font-mono text-[11px] truncate">
                      {formatValue(d.after)}
                    </div>
                  ) : (
                    <div className="flex-1 min-w-0 text-rose-700 font-mono text-[11px] truncate">
                      {formatValue(d.before)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
